// src/AimdocProvider.tsx
import { useEffect, useRef } from "react";
import { Aimdoc } from "@aimdoc/sdk";

// src/context.ts
import { createContext } from "react";
var AimdocContext = createContext(null);

// src/AimdocProvider.tsx
import { jsx } from "react/jsx-runtime";
var CONTAINER_ID = "aimdoc-provider-container";
var DEFAULT_SCRIPT_URL = "https://app.aimdoc.ai/embedded.bundle.js";
function AimdocProvider({
  agentId,
  scriptUrl = DEFAULT_SCRIPT_URL,
  displayOptions,
  children
}) {
  const initializedRef = useRef(false);
  const sdk = useRef(Aimdoc.getInstance()).current;
  const displayOptionsRef = useRef(displayOptions);
  displayOptionsRef.current = displayOptions;
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    let container = document.getElementById(CONTAINER_ID);
    if (!container) {
      container = document.createElement("div");
      container.id = CONTAINER_ID;
      document.body.appendChild(container);
    }
    const initConfig = () => ({
      agentId,
      ...displayOptionsRef.current ? { displayOptions: displayOptionsRef.current } : {}
    });
    const existingScript = document.querySelector(
      `script[src="${scriptUrl}"]`
    );
    if (existingScript) {
      if (window.aimdoc) {
        window.aimdoc.initAgentChat(CONTAINER_ID, initConfig());
      }
    } else {
      const script = document.createElement("script");
      script.src = scriptUrl;
      script.async = true;
      script.onload = () => {
        if (window.aimdoc) {
          window.aimdoc.initAgentChat(CONTAINER_ID, initConfig());
        }
      };
      document.body.appendChild(script);
    }
    return () => {
      const el = document.getElementById(CONTAINER_ID);
      if (el) {
        document.body.removeChild(el);
      }
    };
  }, [agentId, scriptUrl]);
  const serializedDisplayOptions = displayOptions ? JSON.stringify(displayOptions) : null;
  const lastSentRef = useRef(null);
  useEffect(() => {
    if (!serializedDisplayOptions) return;
    if (serializedDisplayOptions === lastSentRef.current) return;
    lastSentRef.current = serializedDisplayOptions;
    sdk.setDisplayOptions(JSON.parse(serializedDisplayOptions));
  }, [serializedDisplayOptions]);
  return /* @__PURE__ */ jsx(AimdocContext.Provider, { value: sdk, children });
}

// src/useAimdoc.ts
import { useContext } from "react";
function useAimdoc() {
  const sdk = useContext(AimdocContext);
  if (!sdk) {
    throw new Error("useAimdoc must be used within an <AimdocProvider>");
  }
  return sdk;
}

// src/AimdocComponentContext.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var AimdocComponentContext = ({
  componentDescription,
  componentName,
  path
}) => {
  return /* @__PURE__ */ jsx2(
    "div",
    {
      "data-aimdoc-component": true,
      "data-aimdoc-description": componentDescription,
      "data-aimdoc-name": componentName,
      "data-aimdoc-path": path,
      style: { display: "none" }
    }
  );
};

// src/useServices.ts
import { useCallback, useEffect as useEffect2, useRef as useRef2, useState } from "react";
var errorMessage = (error) => error instanceof Error ? error.message : "Something went wrong. Please try again.";
function useServices(client, options = {}) {
  const { enabled = true, refreshOnFocus = true } = options;
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const sequence = useRef2(0);
  const active = enabled && !!client;
  const refresh = useCallback(async () => {
    if (!active || !client) return;
    const current = ++sequence.current;
    try {
      const rows = await client.list();
      if (current !== sequence.current) return;
      setServices(rows);
      setError(null);
    } catch (caught) {
      if (current !== sequence.current) return;
      setError(errorMessage(caught));
    } finally {
      if (current === sequence.current) setLoading(false);
    }
  }, [active, client]);
  useEffect2(() => {
    if (!active) {
      setLoading(false);
      return;
    }
    setLoading(true);
    void refresh();
  }, [active, refresh]);
  useEffect2(() => {
    if (!active || !refreshOnFocus || typeof window === "undefined") return;
    const onFocus = () => void refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [active, refreshOnFocus, refresh]);
  const start = useCallback(
    async (key) => {
      if (!client) throw new Error("Aimdoc services client is not configured.");
      const job = await client.start(key);
      void refresh();
      return job;
    },
    [client, refresh]
  );
  const checkout = useCallback(
    (key, checkoutOptions) => {
      if (!client) throw new Error("Aimdoc services client is not configured.");
      return client.checkout(key, checkoutOptions);
    },
    [client]
  );
  return { services, loading, error, refresh, start, checkout };
}

// src/useJobs.ts
import { useCallback as useCallback2, useEffect as useEffect3, useRef as useRef3, useState as useState2 } from "react";
function useJobs(client, options = {}) {
  const { enabled = true, refreshOnFocus = true, refreshIntervalMs = 0 } = options;
  const [jobs, setJobs] = useState2([]);
  const [loading, setLoading] = useState2(true);
  const [error, setError] = useState2(null);
  const sequence = useRef3(0);
  const active = enabled && !!client;
  const refresh = useCallback2(async () => {
    if (!active || !client) return;
    const current = ++sequence.current;
    try {
      const rows = await client.list();
      if (current !== sequence.current) return;
      setJobs(rows);
      setError(null);
    } catch (caught) {
      if (current !== sequence.current) return;
      setError(errorMessage(caught));
    } finally {
      if (current === sequence.current) setLoading(false);
    }
  }, [active, client]);
  useEffect3(() => {
    if (!active) {
      setLoading(false);
      return;
    }
    setLoading(true);
    void refresh();
  }, [active, refresh]);
  useEffect3(() => {
    if (!active || !refreshOnFocus || typeof window === "undefined") return;
    const onFocus = () => void refresh();
    const onVisibility = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [active, refreshOnFocus, refresh]);
  useEffect3(() => {
    if (!active || !refreshIntervalMs || refreshIntervalMs <= 0) return;
    const timer = setInterval(() => void refresh(), refreshIntervalMs);
    return () => clearInterval(timer);
  }, [active, refreshIntervalMs, refresh]);
  const needsYou = jobs.filter((job) => job.open_questions > 0 || job.pending_approvals > 0);
  return { jobs, needsYou, loading, error, refresh };
}

// src/useJob.ts
import { useCallback as useCallback3, useEffect as useEffect4, useRef as useRef4, useState as useState3 } from "react";
var REFRESH_ON_EVENT_KINDS = /* @__PURE__ */ new Set([
  "asked_owner",
  "approval_requested",
  "approval_decided",
  "artifact_published",
  "blocked",
  "unblocked",
  "turned_on",
  "turned_off",
  "owner_changed",
  "routine_changed",
  "messaged",
  "answered"
]);
var WAKE_TERMINAL = /* @__PURE__ */ new Set(["succeeded", "failed", "cancelled", "skipped"]);
function useJob(client, jobId, options = {}) {
  const { enabled = true, live = true, maxEvents = 100 } = options;
  const [job, setJob] = useState3(null);
  const [plan, setPlan] = useState3(null);
  const [wake, setWake] = useState3(null);
  const [connected, setConnected] = useState3(false);
  const [loading, setLoading] = useState3(true);
  const [error, setError] = useState3(null);
  const sequence = useRef4(0);
  const active = enabled && !!client && !!jobId;
  const refresh = useCallback3(async () => {
    if (!active || !client || !jobId) return;
    const current = ++sequence.current;
    try {
      const view = await client.get(jobId);
      if (current !== sequence.current) return;
      setJob(view);
      if (!view.working) setPlan(null);
      setError(null);
    } catch (caught) {
      if (current !== sequence.current) return;
      setError(errorMessage(caught));
    } finally {
      if (current === sequence.current) setLoading(false);
    }
  }, [active, client, jobId]);
  useEffect4(() => {
    setJob(null);
    setPlan(null);
    setWake(null);
    if (!active) {
      setLoading(false);
      return;
    }
    setLoading(true);
    void refresh();
  }, [active, refresh]);
  useEffect4(() => {
    var _a;
    if (!active || !live || !client || !jobId) return;
    const after = ((_a = job == null ? void 0 : job.recent_events) == null ? void 0 : _a.length) ? job.recent_events[job.recent_events.length - 1].seq : 0;
    const unsubscribe = client.subscribe(
      jobId,
      {
        onOpen: ({ reconnect }) => {
          setConnected(true);
          if (reconnect) void refresh();
        },
        onStatus: (frame) => {
          setJob(
            (current) => {
              var _a2;
              return current ? {
                ...current,
                status: frame.status,
                blocked_on: frame.blocked_on,
                headline: (_a2 = frame.headline) != null ? _a2 : current.headline,
                working: frame.working,
                active_wake_id: frame.active_wake_id
              } : current;
            }
          );
          if (!frame.working) setPlan(null);
        },
        onPlan: (next) => setPlan(next),
        onWake: (frame) => {
          setWake(frame);
          setJob(
            (current) => {
              var _a2;
              return current ? {
                ...current,
                working: !WAKE_TERMINAL.has(frame.status) && frame.status !== "waiting",
                active_wake_id: WAKE_TERMINAL.has(frame.status) ? null : frame.wake_id,
                last_wake_summary: (_a2 = frame.summary) != null ? _a2 : current.last_wake_summary
              } : current;
            }
          );
          if (frame.status === "running" || frame.status === "queued") setPlan(null);
          if (WAKE_TERMINAL.has(frame.status) || frame.status === "waiting") void refresh();
        },
        onState: (frame) => {
          setJob(
            (current) => {
              var _a2, _b, _c;
              return current ? {
                ...current,
                state: (_a2 = frame.state) != null ? _a2 : current.state,
                details: (_b = frame.details) != null ? _b : current.details,
                headline: (_c = frame.headline) != null ? _c : current.headline
              } : current;
            }
          );
        },
        onEvent: (event) => {
          setJob((current) => {
            if (!current) return current;
            if (current.recent_events.some((row) => row.seq === event.seq)) return current;
            const recent_events = [...current.recent_events, event].slice(-Math.max(1, maxEvents));
            return { ...current, recent_events };
          });
          if (REFRESH_ON_EVENT_KINDS.has(event.kind)) void refresh();
        },
        onQuestion: (question) => {
          setJob((current) => {
            if (!current) return current;
            const rest = current.open_questions.filter((row) => row.id !== question.id);
            return {
              ...current,
              open_questions: question.status === "open" ? [...rest, question] : rest
            };
          });
        },
        onApproval: (approval) => {
          setJob((current) => {
            if (!current) return current;
            const rest = current.pending_approvals.filter((row) => row.id !== approval.id);
            return {
              ...current,
              pending_approvals: approval.status === "pending" ? [...rest, approval] : rest
            };
          });
        },
        onError: (caught, { willRetry }) => {
          setConnected(false);
          if (!willRetry) setError(errorMessage(caught));
        }
      },
      { after }
    );
    return () => {
      unsubscribe();
      setConnected(false);
    };
  }, [active, live, client, jobId, refresh]);
  const steer = useCallback3(
    async (text, steerOptions) => {
      if (!client || !jobId) throw new Error("No job to steer.");
      const message = await client.steer(jobId, text, steerOptions);
      setJob((current) => current ? { ...current, messages: [...current.messages, message] } : current);
      return message;
    },
    [client, jobId]
  );
  const answer = useCallback3(
    async (requestId, payload) => {
      if (!client || !jobId) throw new Error("No job to answer.");
      const answered = await client.answer(jobId, requestId, payload);
      setJob(
        (current) => current ? { ...current, open_questions: current.open_questions.filter((row) => row.id !== requestId) } : current
      );
      void refresh();
      return answered;
    },
    [client, jobId, refresh]
  );
  const approve = useCallback3(
    async (approvalId, decision, note) => {
      if (!client || !jobId) throw new Error("No job to approve for.");
      const decided = await client.approve(jobId, approvalId, decision, note);
      setJob(
        (current) => current ? { ...current, pending_approvals: current.pending_approvals.filter((row) => row.id !== approvalId) } : current
      );
      return decided;
    },
    [client, jobId]
  );
  return { job, plan, wake, connected, loading, error, refresh, steer, answer, approve };
}

// src/AimdocJobCard.tsx
import { useState as useState4 } from "react";
import { aimdoc } from "@aimdoc/sdk";
import { jsx as jsx3, jsxs } from "react/jsx-runtime";
function describeJobStatus(job) {
  if (job.status === "off") return job.ended_reason ? `Off \u2014 ${job.ended_reason}` : "Off";
  switch (job.blocked_on) {
    case "owner_input":
      return "Needs your answer";
    case "approval":
      return "Needs your approval";
    case "vendor":
      return "Waiting on the team";
    case "budget":
      return "Paused until next period";
    case "owner_missing":
      return "Needs an owner";
  }
  return job.working ? "Working" : "On";
}
function QuestionForm({
  question,
  onAnswer
}) {
  var _a, _b, _c;
  const fields = Object.entries((_b = (_a = question.input_schema) == null ? void 0 : _a.properties) != null ? _b : {});
  const [values, setValues] = useState4(() => {
    var _a2;
    const initial = {};
    for (const [name, field] of fields) {
      initial[name] = (_a2 = field.default) != null ? _a2 : field.type === "boolean" ? false : "";
    }
    return initial;
  });
  const [text, setText] = useState4("");
  const [busy, setBusy] = useState4(false);
  const [error, setError] = useState4(null);
  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (fields.length) await onAnswer({ answers: values });
      else await onAnswer({ text });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not send your answer.");
    } finally {
      setBusy(false);
    }
  };
  const setValue = (name, value) => setValues((current) => ({ ...current, [name]: value }));
  const renderField = (name, field) => {
    var _a2, _b2, _c2, _d, _e, _f;
    const id = `aimdoc-q-${question.id}-${name}`;
    const label = field.title || name;
    if (field.enum) {
      return /* @__PURE__ */ jsxs("label", { htmlFor: id, className: "aimdoc-job-card__field", children: [
        /* @__PURE__ */ jsx3("span", { children: label }),
        /* @__PURE__ */ jsxs("select", { id, value: String((_a2 = values[name]) != null ? _a2 : ""), onChange: (e) => setValue(name, e.target.value), required: (_c2 = (_b2 = question.input_schema) == null ? void 0 : _b2.required) == null ? void 0 : _c2.includes(name), children: [
          /* @__PURE__ */ jsx3("option", { value: "", children: "Choose\u2026" }),
          field.enum.map((option) => /* @__PURE__ */ jsx3("option", { value: String(option), children: String(option) }, String(option)))
        ] })
      ] }, name);
    }
    if (field.type === "boolean") {
      return /* @__PURE__ */ jsxs("label", { htmlFor: id, className: "aimdoc-job-card__field aimdoc-job-card__field--boolean", children: [
        /* @__PURE__ */ jsx3("input", { id, type: "checkbox", checked: Boolean(values[name]), onChange: (e) => setValue(name, e.target.checked) }),
        /* @__PURE__ */ jsx3("span", { children: label })
      ] }, name);
    }
    const numeric = field.type === "number" || field.type === "integer";
    return /* @__PURE__ */ jsxs("label", { htmlFor: id, className: "aimdoc-job-card__field", children: [
      /* @__PURE__ */ jsx3("span", { children: label }),
      field.description && /* @__PURE__ */ jsx3("small", { children: field.description }),
      /* @__PURE__ */ jsx3(
        "input",
        {
          id,
          type: numeric ? "number" : field.format === "date" ? "date" : "text",
          value: String((_d = values[name]) != null ? _d : ""),
          required: (_f = (_e = question.input_schema) == null ? void 0 : _e.required) == null ? void 0 : _f.includes(name),
          onChange: (e) => setValue(name, numeric ? e.target.value === "" ? "" : Number(e.target.value) : e.target.value)
        }
      )
    ] }, name);
  };
  return /* @__PURE__ */ jsxs("form", { className: "aimdoc-job-card__question", onSubmit: submit, children: [
    /* @__PURE__ */ jsx3("p", { className: "aimdoc-job-card__question-text", children: question.question }),
    question.context && /* @__PURE__ */ jsx3("p", { className: "aimdoc-job-card__question-context", children: question.context }),
    fields.length > 0 ? fields.map(([name, field]) => renderField(name, field)) : ((_c = question.options) == null ? void 0 : _c.length) ? /* @__PURE__ */ jsx3("div", { className: "aimdoc-job-card__options", role: "radiogroup", children: question.options.map((option) => /* @__PURE__ */ jsxs("label", { className: "aimdoc-job-card__option", children: [
      /* @__PURE__ */ jsx3("input", { type: "radio", name: `aimdoc-q-${question.id}`, value: option, checked: text === option, onChange: () => setText(option) }),
      /* @__PURE__ */ jsx3("span", { children: option })
    ] }, option)) }) : /* @__PURE__ */ jsx3("textarea", { className: "aimdoc-job-card__answer", value: text, onChange: (e) => setText(e.target.value), rows: 3, required: true }),
    question.default_action && /* @__PURE__ */ jsxs("small", { className: "aimdoc-job-card__default", children: [
      "If you do not answer: ",
      question.default_action
    ] }),
    error && /* @__PURE__ */ jsx3("p", { className: "aimdoc-job-card__error", role: "alert", children: error }),
    /* @__PURE__ */ jsx3("button", { type: "submit", className: "aimdoc-job-card__button", disabled: busy || !fields.length && !text.trim(), children: busy ? "Sending\u2026" : "Answer" })
  ] });
}
function ApprovalRow({
  approval,
  onDecide
}) {
  const [busy, setBusy] = useState4(false);
  const decide = async (approve) => {
    setBusy(true);
    try {
      await onDecide(approve);
    } finally {
      setBusy(false);
    }
  };
  return /* @__PURE__ */ jsxs("div", { className: "aimdoc-job-card__approval", children: [
    /* @__PURE__ */ jsxs("p", { className: "aimdoc-job-card__approval-text", children: [
      "Wants to run ",
      /* @__PURE__ */ jsx3("code", { children: approval.tool }),
      approval.reason ? ` \u2014 ${approval.reason}` : ""
    ] }),
    approval.args && Object.keys(approval.args).length > 0 && /* @__PURE__ */ jsx3("pre", { className: "aimdoc-job-card__approval-args", children: JSON.stringify(approval.args, null, 2) }),
    /* @__PURE__ */ jsxs("div", { className: "aimdoc-job-card__approval-actions", children: [
      /* @__PURE__ */ jsx3("button", { type: "button", className: "aimdoc-job-card__button", disabled: busy, onClick: () => decide(true), children: "Approve" }),
      /* @__PURE__ */ jsx3("button", { type: "button", className: "aimdoc-job-card__button aimdoc-job-card__button--secondary", disabled: busy, onClick: () => decide(false), children: "Decline" })
    ] })
  ] });
}
function AimdocJobCard({ jobId, client, className, eventLimit = 8 }) {
  var _a;
  const { job, plan, loading, error, answer, approve } = useJob(client != null ? client : aimdoc.jobs, jobId);
  if (loading && !job) return /* @__PURE__ */ jsx3("section", { className: ["aimdoc-job-card", className].filter(Boolean).join(" "), "aria-busy": "true" });
  if (!job) {
    return /* @__PURE__ */ jsx3("section", { className: ["aimdoc-job-card", className].filter(Boolean).join(" "), children: error && /* @__PURE__ */ jsx3("p", { className: "aimdoc-job-card__error", role: "alert", children: error }) });
  }
  const events = [...job.recent_events].reverse().slice(0, Math.max(0, eventLimit));
  return /* @__PURE__ */ jsxs("section", { className: ["aimdoc-job-card", className].filter(Boolean).join(" "), "data-status": job.status, "data-blocked-on": (_a = job.blocked_on) != null ? _a : void 0, children: [
    /* @__PURE__ */ jsxs("header", { className: "aimdoc-job-card__header", children: [
      /* @__PURE__ */ jsx3("h3", { className: "aimdoc-job-card__title", children: job.service.name }),
      /* @__PURE__ */ jsx3("span", { className: "aimdoc-job-card__status", children: describeJobStatus(job) })
    ] }),
    job.headline && /* @__PURE__ */ jsx3("p", { className: "aimdoc-job-card__headline", children: job.headline }),
    error && /* @__PURE__ */ jsx3("p", { className: "aimdoc-job-card__error", role: "alert", children: error }),
    plan && plan.items.length > 0 && /* @__PURE__ */ jsx3("ol", { className: "aimdoc-job-card__plan", children: plan.items.map((item) => /* @__PURE__ */ jsxs("li", { className: "aimdoc-job-card__plan-item", "data-status": item.status, children: [
      /* @__PURE__ */ jsx3("span", { children: item.title }),
      item.status === "in_progress" && item.note && /* @__PURE__ */ jsx3("small", { children: item.note })
    ] }, item.id)) }),
    job.open_questions.map((question) => /* @__PURE__ */ jsx3(QuestionForm, { question, onAnswer: (payload) => answer(question.id, payload) }, question.id)),
    job.pending_approvals.map((pending) => /* @__PURE__ */ jsx3(ApprovalRow, { approval: pending, onDecide: (decision) => approve(pending.id, decision) }, pending.id)),
    events.length > 0 && /* @__PURE__ */ jsx3("ul", { className: "aimdoc-job-card__events", children: events.map((event) => /* @__PURE__ */ jsxs("li", { className: "aimdoc-job-card__event", "data-kind": event.kind, children: [
      /* @__PURE__ */ jsx3("span", { children: event.summary }),
      event.created_at && /* @__PURE__ */ jsx3("time", { dateTime: event.created_at, children: new Date(event.created_at).toLocaleString() })
    ] }, event.id)) })
  ] });
}

// src/useAimdocTool.ts
import { useEffect as useEffect5, useRef as useRef5 } from "react";
import { aimdoc as aimdoc2 } from "@aimdoc/sdk";
function useAimdocTool(tool, options = {}) {
  var _a, _b;
  const executeRef = useRef5(tool.execute);
  executeRef.current = tool.execute;
  const { agentId, enabled = true } = options;
  const { name, title, description, timeoutMs } = tool;
  const inputSchemaKey = JSON.stringify((_a = tool.inputSchema) != null ? _a : null);
  const annotationsKey = JSON.stringify((_b = tool.annotations) != null ? _b : null);
  useEffect5(() => {
    var _a2, _b2;
    if (!enabled) return;
    try {
      return aimdoc2.registerTool(
        {
          name,
          title,
          description,
          timeoutMs,
          inputSchema: (_a2 = JSON.parse(inputSchemaKey)) != null ? _a2 : void 0,
          annotations: (_b2 = JSON.parse(annotationsKey)) != null ? _b2 : void 0,
          execute: (input, executeOptions) => executeRef.current(input, executeOptions)
        },
        { agentId }
      );
    } catch (error) {
      console.error(error);
      return;
    }
  }, [enabled, agentId, name, title, description, timeoutMs, inputSchemaKey, annotationsKey]);
}

// src/useObjectives.ts
import { useCallback as useCallback4, useEffect as useEffect6, useRef as useRef6, useState as useState5 } from "react";
var errorMessage2 = (error) => error instanceof Error ? error.message : "Something went wrong. Please try again.";
function useObjectives(client, options = {}) {
  const { agentId, refreshOnFocus = true, refreshIntervalMs = 0, enabled = true } = options;
  const [objectives, setObjectives] = useState5([]);
  const [loading, setLoading] = useState5(true);
  const [error, setError] = useState5(null);
  const sequence = useRef6(0);
  const active = enabled && !!client;
  const refresh = useCallback4(async () => {
    if (!active || !client) return;
    const current = ++sequence.current;
    try {
      const rows = await client.listObjectives(agentId ? { agentId } : void 0);
      if (current !== sequence.current) return;
      setObjectives(rows);
      setError(null);
    } catch (caught) {
      if (current !== sequence.current) return;
      setError(errorMessage2(caught));
    } finally {
      if (current === sequence.current) setLoading(false);
    }
  }, [active, client, agentId]);
  useEffect6(() => {
    if (!active) {
      setLoading(false);
      return;
    }
    setLoading(true);
    void refresh();
  }, [active, refresh]);
  useEffect6(() => {
    if (!active || !refreshOnFocus || typeof window === "undefined") return;
    const onFocus = () => void refresh();
    const onVisibility = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [active, refreshOnFocus, refresh]);
  useEffect6(() => {
    if (!active || !refreshIntervalMs || refreshIntervalMs <= 0) return;
    const timer = setInterval(() => void refresh(), refreshIntervalMs);
    return () => clearInterval(timer);
  }, [active, refreshIntervalMs, refresh]);
  const completed = objectives.filter((objective) => objective.status === "done");
  const incomplete = objectives.filter((objective) => objective.status !== "done");
  return { objectives, incomplete, completed, loading, error, refresh };
}
export {
  AimdocComponentContext,
  AimdocJobCard,
  AimdocProvider,
  describeJobStatus,
  useAimdoc,
  useAimdocTool,
  useJob,
  useJobs,
  useObjectives,
  useServices
};
